/**
 * ReviewTeamBar — Persistent team assignment bar for project lifecycle view
 * Displays: Owner, VA(s), Support team, External experts, Deadline
 * Stage-aware visibility: INBOX/LONG_LIST show "Owner" only, IN_REVIEW+ show full bar
 * Sprint 8 — S8-08 (updated: ADDED_BY → OWNER, all roles from assignments, avatars from DB)
 */

import { useEffect, useState } from 'react'
import type { Assignment, LifecycleStage } from '../../types'
import { authFetchJson } from '../../lib/authFetch'

export interface ReviewTeamBarProps {
  projectId: number
  currentStage: string
  /** Fallback owner info for stages without assignments (INBOX/LONG_LIST) */
  fallbackOwner?: { name: string; avatar_url?: string }
}

const STAGE_ORDER: Record<LifecycleStage, number> = {
  INBOX: 0,
  LONG_LIST: 1,
  IN_REVIEW: 2,
  EXTRA_MILE: 3,
  VB_PROCESS: 4,
  FUNDED: 5,
  ALL: -1,
}

function shouldFetchAssignments(stage: string): boolean {
  const stageLevel = STAGE_ORDER[stage as LifecycleStage] ?? 0
  return stageLevel >= STAGE_ORDER.IN_REVIEW
}

/* ─── Avatar ────────────────────────────────────────────── */

interface AvatarProps {
  name?: string
  avatarUrl?: string | null
  size?: number
  border?: string
}

function Avatar({ name = 'Unknown', avatarUrl, size = 26, border }: AvatarProps) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0]?.toUpperCase() || '')
    .join('')

  return (
    <div
      className="flex-shrink-0 rounded-full flex items-center justify-center text-xs font-medium"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundImage: avatarUrl ? `url('${avatarUrl}')` : undefined,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundColor: !avatarUrl ? '#4a4a6a' : undefined,
        color: !avatarUrl ? '#e8e8f0' : undefined,
        border: border || 'none',
      }}
    >
      {!avatarUrl && initials}
    </div>
  )
}

/* ─── Section helpers ───────────────────────────────────── */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold uppercase text-[#a8a8c0] tracking-wide mb-0.5">
      {children}
    </div>
  )
}

function PersonCard({ name, subtitle, avatarUrl, avatarBorder }: {
  name: string
  subtitle?: string | null
  avatarUrl?: string | null
  avatarBorder?: string
}) {
  return (
    <div className="flex items-center gap-2">
      <Avatar name={name} avatarUrl={avatarUrl} size={26} border={avatarBorder} />
      <div className="min-w-0">
        <div className="text-[12px] font-medium text-[#e8e8f0] truncate">{name}</div>
        {subtitle && (
          <div className="text-[10px] text-[#a8a8c0] truncate">{subtitle}</div>
        )}
      </div>
    </div>
  )
}

/* ─── Main component ────────────────────────────────────── */

export function ReviewTeamBar({
  projectId,
  currentStage,
  fallbackOwner,
}: ReviewTeamBarProps) {
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [loading, setLoading] = useState(false)

  const showFullBar = shouldFetchAssignments(currentStage)

  useEffect(() => {
    if (!showFullBar) {
      setAssignments([])
      return
    }

    let cancelled = false
    const fetchAssignments = async () => {
      try {
        setLoading(true)
        const data = await authFetchJson<{ assignments: Assignment[] }>(
          `/api/v2/projects/${projectId}/assignments`
        )
        if (!cancelled) setAssignments(data?.assignments || [])
      } catch (err) {
        console.error('ReviewTeamBar: fetch failed', { projectId, error: err })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchAssignments()
    return () => { cancelled = true }
  }, [projectId, showFullBar])

  // Group by role
  const ownerAssignment = assignments.find(a => a.role === 'OWNER')
  const vaAssignments = assignments.filter(a => a.role === 'VENTURE_ARCHITECT')
  const supportAssignments = assignments.filter(a => a.role === 'SUPPORT')
  const externalAssignments = assignments.filter(a => a.role === 'EXTERNAL')
  const deadline = assignments.find(a => a.deadline)?.deadline || null

  // Owner display: from assignments or fallback
  const ownerName = ownerAssignment?.display_name || fallbackOwner?.name
  const ownerAvatar = ownerAssignment?.avatar_url || fallbackOwner?.avatar_url
  const ownerTitle = ownerAssignment?.role_title

  return (
    <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-lg px-4 py-2.5 flex items-center gap-5 overflow-x-auto">
      {/* OWNER — always visible */}
      {ownerName && (
        <div className="flex-shrink-0">
          <SectionLabel>Owner</SectionLabel>
          <PersonCard name={ownerName} subtitle={ownerTitle} avatarUrl={ownerAvatar} />
        </div>
      )}

      {/* Full bar — IN_REVIEW+ */}
      {showFullBar && !loading && (
        <>
          {/* Vertical divider */}
          {(vaAssignments.length > 0 || supportAssignments.length > 0) && (
            <div className="w-px h-10 bg-[#2a2a44] flex-shrink-0" />
          )}

          {/* VA section — show each VA individually */}
          {vaAssignments.length > 0 && (
            <div className="flex-shrink-0">
              <SectionLabel>VA</SectionLabel>
              <div className="flex items-center gap-3">
                {vaAssignments.map(va => (
                  <PersonCard
                    key={va.id}
                    name={va.display_name}
                    subtitle={va.role_title}
                    avatarUrl={va.avatar_url}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Divider before support */}
          {supportAssignments.length > 0 && (
            <div className="w-px h-10 bg-[#2a2a44] flex-shrink-0" />
          )}

          {/* VB Team section — show each with name + role_title */}
          {supportAssignments.length > 0 && (
            <div className="flex-shrink-0">
              <SectionLabel>VB Team</SectionLabel>
              <div className="flex items-center gap-3">
                {supportAssignments.map(s => (
                  <PersonCard
                    key={s.id}
                    name={s.display_name}
                    subtitle={s.role_title}
                    avatarUrl={s.avatar_url}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Divider before external */}
          {externalAssignments.length > 0 && (
            <div className="w-px h-10 bg-[#2a2a44] flex-shrink-0" />
          )}

          {/* EXTERNAL section — dashed border avatars */}
          {externalAssignments.length > 0 && (
            <div className="flex-shrink-0">
              <SectionLabel>External</SectionLabel>
              <div className="flex items-center gap-3">
                {externalAssignments.map(ext => (
                  <PersonCard
                    key={ext.id}
                    name={ext.display_name}
                    subtitle={ext.role_title}
                    avatarUrl={ext.avatar_url}
                    avatarBorder="2px dashed #a8a8c0"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Divider before deadline */}
          {deadline && <div className="w-px h-10 bg-[#2a2a44] flex-shrink-0" />}

          {/* DEADLINE */}
          {deadline && (
            <div className="flex-shrink-0">
              <SectionLabel>Deadline</SectionLabel>
              <div className="text-[12px] font-medium text-[#e8e8f0]">
                {new Date(deadline).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                })}
              </div>
            </div>
          )}
        </>
      )}

      {showFullBar && loading && (
        <div className="text-[12px] text-[#a8a8c0]">Loading team...</div>
      )}
    </div>
  )
}

export default ReviewTeamBar
